import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Kicker } from '@/components/Kicker';
import { CheckCircle2, XCircle, Clock } from 'lucide-react';

function StatusBadge({ status, error }) {
  const map = {
    sent: { cls: 'bg-[#E7F6EE] text-[#1F7A4D]', icon: CheckCircle2, label: 'Gesendet' },
    queued: { cls: 'bg-[#FFF3E0] text-[#B7791F]', icon: Clock, label: 'Wartend' },
    failed: { cls: 'bg-[#FBEBEA] text-[#B42318]', icon: XCircle, label: 'Fehlgeschlagen' },
  };
  const v = map[status] || map.queued;
  const Icon = v.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${v.cls}`}>
      <Icon className="h-3.5 w-3.5" /> {v.label}
    </span>
  );
}

export default function AdminNotifications() {
  const { t } = useTranslation();
  const q = useQuery({ queryKey: ['admin', 'notifications'], queryFn: async () => (await api.get('/admin/notifications')).data });
  const items = q.data?.items || [];
  return (
    <section>
      <Kicker>{t('admin.title')}</Kicker>
      <h1 className="mt-3 font-display text-3xl font-semibold text-[#14171C]">{t('admin.nav.notifications')}</h1>
      <p className="mt-2 max-w-2xl text-sm text-[#4A4D52]">{t('admin.notifications.hint')}</p>

      <div className="mt-8 pn-card overflow-hidden">
        <table className="w-full text-left text-sm" data-testid="admin-notifications-table">
          <thead className="bg-[#F6F8FB] text-xs uppercase tracking-wide text-[#4A4D52]">
            <tr>
              <th className="px-4 py-3">Zeit</th>
              <th className="px-4 py-3">Empfänger</th>
              <th className="px-4 py-3">Produkt / Preis</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Provider-ID / Fehler</th>
            </tr>
          </thead>
          <tbody>
            {q.isLoading && <tr><td colSpan={5} className="p-6 text-center text-[#4A4D52]">{t('common.loading')}</td></tr>}
            {items.map((n) => (
              <tr key={n.id} className="border-t border-[#E4E9F0] align-top hover:bg-[#F6F8FB]">
                <td className="px-4 py-3 font-mono text-xs text-[#4A4D52]">{new Date(n.created_at).toLocaleString()}</td>
                <td className="px-4 py-3">
                  <div>{n.to_name}</div>
                  <div className="font-mono text-xs text-[#4A4D52]">{n.to_email}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="font-medium">{n.product_name}</div>
                  <div className="font-mono text-xs text-[#E2611C]">{n.price?.toFixed(2)} {n.currency}/{n.unit}</div>
                </td>
                <td className="px-4 py-3"><StatusBadge status={n.status} error={n.error} /></td>
                <td className="px-4 py-3 text-xs">
                  {n.provider_id && <div className="font-mono text-[11px] text-[#4A4D52]">{n.provider_id}</div>}
                  {n.error && <div className="text-[#B42318] break-words max-w-[380px]">{n.error}</div>}
                </td>
              </tr>
            ))}
            {!q.isLoading && items.length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center text-[#4A4D52]">{t('catalog.empty')}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
